/* @flow */

import * as React from 'react';

import type {DisplayOptionEntity} from 'nutshell-core/utils';

import {Avatar, IconAvatar} from 'shells/avatar';
import {colors} from 'shells/colors';
import {ContactIcon, TrashOutlineIcon} from 'shells/icon';
import {Stack} from 'shells/layout';
import {ContactCard} from 'shells/peep-cards/contact-card';
import {PotentialPeepsDropdown} from 'shells/peep-cards/potential-peeps-dropdown/potential-peeps-dropdown';
import {Popover} from 'shells/popover';
import {PopoverForm} from 'shells/popover-form';
import {TextField} from 'shells/textfield';
import {Body} from 'shells/typography';

import {getPotentialContacts} from '../../../core/form/fields/peeps/contacts/rich-contact-select-helpers';
import {useValidatedAvatarUrl} from '../../../core/hooks/use-validated-avatar-url';

import type {ExternalAttendeeToContactData} from './participants-section';

type Props = {|
    anchor: ?HTMLElement,
    onClose: () => void,
    onSubmit: (data: ExternalAttendeeToContactData) => void,
    data: ExternalAttendeeToContactData,
    isSubmitting: boolean,
|};

export function AttendeeToContactCreatePopover(props: Props) {
    const [name, setName] = React.useState<string>(props.data.name || '');
    const [selectedContact, setSelectedContact] = React.useState<DisplayOptionEntity | null>(null);
    const [potentialContacts, setPotentialContacts] = React.useState<DisplayOptionEntity[]>([]);
    const [showPotentialContacts, setShowPotentialContacts] = React.useState<boolean>(false);
    const [isNameFocused, setIsNameFocused] = React.useState<boolean>(false);

    const validatedAvatarUrl = useValidatedAvatarUrl(
        selectedContact && selectedContact.avatarUrl ? selectedContact.avatarUrl : null
    );

    const nameFieldRef = React.useRef<?HTMLInputElement>(null);

    const label = React.useMemo(() => {
        if (props.data.email) {
            return 'Email address';
        }
        if (props.data.phone) {
            return 'Phone number';
        }
    }, [props.data.email, props.data.phone]);

    const titleSuffix = React.useMemo(() => {
        if (props.data.email) {
            return 'email';
        }
        if (props.data.phone) {
            return 'number';
        }

        return 'activity';
    }, [props.data.email, props.data.phone]);

    const shouldAllowCreate = React.useMemo(() => {
        return showPotentialContacts && name.length > 0;
    }, [showPotentialContacts, name]);

    React.useEffect(() => {
        // If user is editing and the name no longer matches the selected contact, clear selection
        if (selectedContact && selectedContact.value !== name) {
            setSelectedContact(null);
        }

        // Only fetch if there's a name to search for and the name field is focused
        if (name.length === 0 || !isNameFocused) {
            setPotentialContacts([]);
            setShowPotentialContacts(false);

            return;
        }

        // Fetch potential contacts based on the current name
        getPotentialContacts({
            contactInput: {
                name,
                email: null,
                phoneNumber: null,
                description: null,
            },
            relatedAccountIds: [],
            alreadyAddedContactsIds: [],
        }).then((matchedContacts: DisplayOptionEntity[]) => {
            setPotentialContacts(matchedContacts);
            // Show dropdown if there are results and no contact is selected yet
            if (matchedContacts.length > 0 && !selectedContact) {
                setShowPotentialContacts(true);
            }
        });
    }, [name, props.data.name, selectedContact, isNameFocused]);

    const handleClearSelection = () => {
        setSelectedContact(null);
        setShowPotentialContacts(false);
        setName('');

        // Focus the name field after clearing
        setTimeout(() => {
            if (nameFieldRef.current) {
                nameFieldRef.current.focus();
            }
        }, 0);
    };

    return (
        <Popover anchor={props.anchor}>
            <PopoverForm
                title={`Find or create a person to associate with this ${titleSuffix}`}
                allowTitleWrapping={true}
                width={300}
                onClose={props.onClose}
                onSubmit={() =>
                    props.onSubmit({
                        ...props.data,
                        name,
                        contactId: selectedContact ? selectedContact.id : undefined,
                    })
                }
                isSaving={props.isSubmitting}
                saveButtonText={selectedContact ? 'Associate person' : 'Create person'}
                saveButtonSavingText={selectedContact ? 'Associating...' : 'Creating...'}
                saveButtonSuccessText={selectedContact ? 'Associated!' : 'Created!'}
            >
                <div className='flex flex-dir-col gap-16 pb-16'>
                    {(props.data.email || props.data.phone) && (
                        <Stack spacing={4}>
                            <Body>{label}</Body>
                            <TextField
                                name='phone-email-value'
                                value={props.data.email || props.data.phone || ''}
                                disabled={true}
                            />
                        </Stack>
                    )}
                    {selectedContact ? (
                        <ContactCard
                            name={selectedContact.value}
                            description={selectedContact.entityDescription}
                            phoneNumber={selectedContact.primaryPhoneText || props.data.phone}
                            email={selectedContact.primaryEmailText || props.data.email}
                            icon={
                                <>
                                    {validatedAvatarUrl ? (
                                        <Avatar
                                            avatarUrl={validatedAvatarUrl}
                                            size={32}
                                            circular={false}
                                            hasBorderRadius={true}
                                        />
                                    ) : (
                                        <IconAvatar
                                            icon={ContactIcon}
                                            avatarSize={32}
                                            iconSize={16}
                                            iconColor={colors.person}
                                            backgroundColor={colors.blueXlt}
                                            shape='square'
                                        />
                                    )}
                                </>
                            }
                            toolbarProps={{
                                options: [
                                    {
                                        id: 'clear',
                                        icon: TrashOutlineIcon,
                                        tooltipText: 'Remove selection',
                                        onClick: handleClearSelection,
                                    },
                                ],
                            }}
                        />
                    ) : (
                        <Stack spacing={4}>
                            <Body>Name</Body>
                            <TextField
                                name='name'
                                placeholder='Type to search or create a person'
                                value={name}
                                onChange={(value) => setName(value)}
                                textFieldRef={nameFieldRef}
                                autoFocus={true}
                                focusOnMount={true}
                                onFocus={() => {
                                    setIsNameFocused(true);
                                }}
                                onBlur={() => {
                                    // Wait for onSelectPeep to complete
                                    setTimeout(() => {
                                        setIsNameFocused(false);
                                    }, 200);
                                }}
                            />
                            {showPotentialContacts && (
                                <PotentialPeepsDropdown
                                    potentialPeeps={potentialContacts}
                                    onSelectPeep={(contact: DisplayOptionEntity) => {
                                        setName(contact.value);
                                        setSelectedContact(contact);
                                        setShowPotentialContacts(false);
                                    }}
                                    popoverRef={{current: nameFieldRef.current}}
                                    isPopover={true}
                                    isInModal={true}
                                    createButtonProps={{
                                        shouldShow: shouldAllowCreate,
                                        onCreate: () => {
                                            setShowPotentialContacts(false);
                                        },
                                        entityName: name,
                                        entityType: 'person',
                                    }}
                                />
                            )}
                        </Stack>
                    )}
                </div>
            </PopoverForm>
        </Popover>
    );
}
